/**
 * DataGo — product and cart events, published from the theme.
 *
 * These moved out of the custom pixel because the Web Pixels sandbox is handed a
 * reduced product: no tags (so no department for `item_category`), no option
 * values (so no `item_color`) and no compare-at price (so no real `old_price`).
 * Here the item comes from Liquid, through `data-dg-item` on product cards, cart
 * rows and the product page, so every field is populated.
 *
 * The AJAX cart payload is only a fallback: it carries no product tags either.
 */
(function () {
  if (window.__dataGoCartEventsInitialized) return;
  window.__dataGoCartEventsInitialized = true;

  const COLOUR_OPTION_PATTERN = /colou?r/i;

  /**
   * @param {string|undefined} value
   * @returns {object|null}
   */
  const parseItem = (value) => {
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('[DataGo] malformed data-dg-item:', value);
      return null;
    }
  };

  const toMajorUnits = (cents) => (typeof cents === 'number' ? Math.round(cents) / 100 : null);

  const skuArticle = (sku) => {
    if (!sku) return null;
    const parts = sku.split('-');
    return parts.length >= 2 ? parts.slice(0, 2).join('-') : sku;
  };

  /**
   * Fallback mapping from a Cart AJAX line. Mirrors `snippets/datago-items.liquid`
   * except for `item_category`, which needs product tags the AJAX payload omits.
   *
   * @param {object} line
   * @returns {object}
   */
  const itemFromCartLine = (line) => {
    const options = Array.isArray(line.options_with_values) ? line.options_with_values : [];
    const colour = options.find((option) => COLOUR_OPTION_PATTERN.test((option && option.name) || ''));
    const itemColor = colour ? colour.value : null;
    const article = skuArticle(line.sku);

    let itemVariant = line.variant_title || null;
    if (article) itemVariant = itemColor ? `${article} || ${itemColor}` : article;

    const price = toMajorUnits(typeof line.final_price === 'number' ? line.final_price : line.price);
    const original = toMajorUnits(line.original_price);

    return {
      item_id: line.product_id ?? null,
      item_name: line.product_title || line.title || null,
      item_brand: line.vendor || 'LIME',
      item_article: article,
      item_category: null,
      item_category3: line.product_type || null,
      item_variant: itemVariant,
      item_color: itemColor,
      price,
      old_price: original !== null && price !== null && original > price ? original : price,
      quantity: line.quantity ?? 1,
    };
  };

  /**
   * The item a control belongs to: the nearest annotated ancestor, falling back to
   * the product page's own item.
   *
   * @param {Element|null} element
   * @returns {object|null}
   */
  const itemNear = (element) => {
    const host = element && element.closest ? element.closest('[data-dg-item]') : null;
    if (host) return parseItem(host.dataset.dgItem);

    const page = document.querySelector('[data-dg-product]');
    return page ? parseItem(page.dataset.dgProduct) : null;
  };

  const valueOf = (items) => {
    const total = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);

    return Math.round(total * 100) / 100;
  };

  /**
   * Emits `add_to_cart`. `action` distinguishes a new line from bumping an
   * existing one, which the pixel could only guess at from a stale page snapshot.
   *
   * @param {object} response - `/cart/add.js` response.
   * @param {Element} [origin] - The control that was used.
   * @param {string} [action] - `add_item` (default) or `increase_item`.
   */
  const trackAddToCart = (response, origin, action) => {
    if (!window.DataGo) return;

    const lines = Array.isArray(response && response.items) ? response.items : [response];
    const fromDom = itemNear(origin);

    const items = lines.filter(Boolean).map((line) => {
      const mapped = itemFromCartLine(line);
      // Prefer the Liquid item when the DOM offers one — it carries the department.
      return fromDom && fromDom.item_id === mapped.item_id ? { ...fromDom, quantity: mapped.quantity } : mapped;
    });
    if (!items.length) return;

    window.DataGo.refreshCart();
    window.DataGo.push('add_to_cart', { action: action || 'add_item' }, { value: valueOf(items), items });
  };

  /**
   * @param {object[]} previousItems - Cart lines before the change.
   * @param {object[]} nextItems - Cart lines after the change.
   * @param {Object<string,string>} [rowsByIndex] - `data-dg-item` per 1-based row index, read before the change.
   */
  const trackCartChange = (previousItems, nextItems, rowsByIndex) => {
    if (!window.DataGo) return;

    const rows = rowsByIndex || {};
    const before = Array.isArray(previousItems) ? previousItems : [];
    const after = Array.isArray(nextItems) ? nextItems : [];

    const remaining = new Map();
    for (const line of after) remaining.set(line.key, line.quantity || 0);

    let changed = 0;

    before.forEach((line, position) => {
      const previousQuantity = line.quantity || 0;
      const nextQuantity = remaining.has(line.key) ? remaining.get(line.key) : 0;
      const delta = nextQuantity - previousQuantity;
      remaining.delete(line.key);
      if (delta === 0) return;

      const base = parseItem(rows[String(position + 1)]) || itemFromCartLine(line);
      if (!base) return;

      const item = { ...base, quantity: Math.abs(delta) };
      const ecommerce = { value: valueOf([item]), items: [item] };
      changed += 1;

      if (delta > 0) {
        window.DataGo.push('add_to_cart', { action: 'increase_item' }, ecommerce);
        return;
      }

      window.DataGo.push(
        'remove_from_cart',
        { action: nextQuantity === 0 ? 'remove_item' : 'decrease_item' },
        ecommerce
      );
    });

    // A line that only exists after the change is a fresh add.
    for (const [key, quantity] of remaining) {
      const line = after.find((entry) => entry.key === key);
      const base = line ? itemFromCartLine(line) : null;
      if (!base || !quantity) continue;

      const item = { ...base, quantity };
      changed += 1;
      window.DataGo.push('add_to_cart', { action: 'add_item' }, { value: valueOf([item]), items: [item] });
    }

    if (changed) window.DataGo.refreshCart();
  };

  /**
   * All annotated cart rows currently on the page.
   *
   * @returns {object[]}
   */
  const cartItems = () =>
    Array.from(document.querySelectorAll('.cart-item[data-dg-item]'))
      .map((row) => parseItem(row.dataset.dgItem))
      .filter(Boolean);

  const sendViewCart = () => {
    if (!window.DataGo) return;

    const items = cartItems();
    if (!items.length) return;

    window.DataGo.push('view_cart', {}, { value: valueOf(items), items });
  };

  // begin_checkout stays with the pixel: `checkout_started` fires on the real
  // checkout, which this file cannot see.
  const init = () => {
    document.addEventListener('cart-drawer:opened', sendViewCart);

    const page = window.DataGoContext && window.DataGoContext.page;
    if (page && page.template === 'cart') sendViewCart();
  };

  window.DataGoCart = Object.freeze({ trackAddToCart, trackCartChange, sendViewCart });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
